import {AuthenticationResult, BrowserAuthOptions, PublicClientApplication} from "@azure/msal-browser";
import {IConfiguration} from "../Interfaces/IConfiguration";
import * as microsoftTeams from "@microsoft/teams-js";
import {getConfiguration} from "../services/ConfigurationContext/ConfigurationContext.hook";
import {ITokenResult} from "../Interfaces/ITokenResult";

const msalRedirectionKey = "msal_redirecting";

const inIframe = window.self !== window.top;

let accessTokenResult: ITokenResult | undefined = undefined;

let isTeamsApp: boolean | undefined = undefined;

let msalInstance: PublicClientApplication | undefined = undefined;

let configuration: IConfiguration | null = getConfiguration();

initMsalInstance(configuration?.clientId);

function initMsalInstance(clientId: string | undefined) {
    if (!clientId) return;
    const redirectUri = "https://" + configuration?.baseUrl + (inIframe ? "/blank-auth-end.html" : "/messages");
    const authData: BrowserAuthOptions = {
        clientId: clientId,
        authority: "https://login.microsoftonline.com/organizations",
        navigateToLoginRequestUrl: true,
        redirectUri: redirectUri,
        postLogoutRedirectUri: redirectUri,
    };
    msalInstance = new PublicClientApplication({
        auth: authData, cache: {cacheLocation: "sessionStorage", storeAuthStateInCookie: false}
    });
    if (inIframe) return;
    msalInstance.handleRedirectPromise().then((tokenResponse) => {
        if (!tokenResponse) return;
        sessionStorage.setItem(msalRedirectionKey, JSON.stringify(tokenResponse));
    }).catch((error) => {
        console.error(error)
    });
}

function clearTokenIn30min() {
    setTimeout(() => accessTokenResult = undefined, 1800000);
}

const getRedirectionResult = (): null | Record<string, {}> => {
    const stringItem = sessionStorage.getItem(msalRedirectionKey) ?? "";
    if (stringItem.length === 0) return null;
    const parsedItem = JSON.parse(stringItem);
    if (parsedItem === null) return {redirecting: true};
    return parsedItem;
}

const isAppFromTeams = (): Promise<boolean> => {
    return new Promise<boolean>(async resolve => {
        const parentUrl = (window.location != window.parent.location) ? document.referrer : document.location.href;
        if (!parentUrl.includes("teams.cloud.microsoft")) {
            isTeamsApp = false;
            return resolve(isTeamsApp);
        }
        try {
            const timeout = setTimeout(() => {
                isTeamsApp = false;
                return resolve(isTeamsApp);
            }, 300);
            window.onmessage = (event) => {
                if (event.origin.includes("teams.cloud.microsoft")) {
                    clearTimeout(timeout);
                    isTeamsApp = true;
                    return resolve(isTeamsApp);
                }
            }
            await microsoftTeams.app.initialize();
        } catch (e) {
            isTeamsApp = false;
            return resolve(isTeamsApp);
        }
    })
}

const getAccessToken = async (): Promise<ITokenResult | undefined> => {
    if (!!accessTokenResult) return accessTokenResult;
    configuration = getConfiguration();
    if (msalInstance === undefined) initMsalInstance(configuration?.clientId);
    if (isTeamsApp === undefined) await isAppFromTeams();
    return new Promise<ITokenResult>(async (resolve) => {
        if (!configuration) throw new Error("Can't get configuration");
        const accessScope = `${configuration?.accessScope}`;
        if (!isTeamsApp) accessTokenResult = await getTokenWithScopesFromWeb([accessScope]);
        else accessTokenResult = await getAccessTokenFromTeams();
        clearTokenIn30min();
        return resolve(accessTokenResult);
    })
}

const consentScopes = async (scopes: Array<string>): Promise<void> => {
    configuration = getConfiguration();
    if (msalInstance === undefined) initMsalInstance(configuration?.clientId);
    await msalInstance?.loginRedirect({scopes, prompt: "consent"});
}

const msalLogin = (scopes: Array<string>, prompt: "select_account" | "consent") => {
    return new Promise<ITokenResult | undefined>(async (resolve, reject) => {
        if (!msalInstance) reject("MSAL instance is not initialized");
        if (inIframe) {
            const authResult = await msalInstance!.loginPopup({scopes, prompt});
            return resolve(extractTokenResult(authResult));
        } else {
            await msalInstance!.loginRedirect({scopes, prompt});
            return resolve(undefined);
        }
    })
}

const getTokenWithScopesFromWeb = (scopes: Array<string>): Promise<ITokenResult> => {
    return new Promise<ITokenResult>(async resolve => {
        if (!msalInstance) throw new Error("MSAL instance is not initialized");
        configuration = getConfiguration();
        if (!configuration) throw new Error("Can't get configuration");
        try {
            let redirectionResult = await getRedirectionResult();
            if (redirectionResult?.redirecting) {
                const resetTimeout = setTimeout(() => {
                    sessionStorage.removeItem(msalRedirectionKey);
                    window.location.reload();
                }, 3000);
                const interval = setInterval(() => {
                    redirectionResult = getRedirectionResult();
                    if (!("authority" in (redirectionResult ?? {}))) return;
                    clearTimeout(resetTimeout);
                    clearInterval(interval);
                    sessionStorage.removeItem(msalRedirectionKey);
                    return resolve(extractTokenResult(redirectionResult as AuthenticationResult));
                }, 500);
            } else {
                const userAccount = msalInstance.getAllAccounts()[0];
                if (!userAccount) throw new Error("User not logged in");
                const loginResponse = await msalInstance.ssoSilent({
                    scopes,
                    loginHint: userAccount.username,
                    extraQueryParameters: {domain_hint: "organizations"},
                });
                sessionStorage.removeItem(msalRedirectionKey);
                return resolve(extractTokenResult(loginResponse));
            }
        } catch (err) {
            sessionStorage.setItem(msalRedirectionKey, JSON.stringify(null));
            try {
                const result = await msalLogin(scopes, "select_account");
                if (!!result) return resolve(result);
            } catch (err) {
                const result = await msalLogin(scopes, "consent");
                if (!!result) return resolve(result);
            }
        }
    })
}

const extractTokenResult = (result: AuthenticationResult): ITokenResult => {
    return {
        authResult: true,
        token: result.accessToken,
        tenantId: result.tenantId,
        userId: result.uniqueId,
        userMail: result.account?.username ?? "",
        userName: result.account?.name ?? "",
    }
}

const getAccessTokenFromTeams = async (): Promise<ITokenResult> => {
    let token = await microsoftTeams.authentication.getAuthToken({silent: true});
    if (token) return {authResult: true, token};
    else {
        microsoftTeams.app.notifySuccess();
        token = await microsoftTeams.authentication.getAuthToken({silent: false});
        if (token) return {authResult: true, token};
        return {authResult: false};
    }
}

const signOut = async () => {
    if (!msalInstance) throw new Error("MSAL instance is not initialized");
    const logoutRequest = {
        account: msalInstance.getAllAccounts()[0],
    };
    await msalInstance.logoutRedirect(logoutRequest);
}

export const TokenModule = {
    getAccessToken,
    isAppFromTeams,
    signOut,
    consentScopes
}